<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import ConfirmDeleteModalHost from '@/components/current-student-advanced-standing-form/shared/ConfirmDeleteModalHost.vue'
import ExternalCourseModalHost from '@/components/current-student-advanced-standing-form/modals/external-course/ExternalCourseModalHost.vue'
import ExternalSubjectModalHost from '@/components/current-student-advanced-standing-form/modals/external-subject/ExternalSubjectModalHost.vue'
import SubjectRequestModalHost from '@/components/current-student-advanced-standing-form/modals/subject-request/SubjectRequestModalHost.vue'
import FormWizardSections from '@/components/current-student-advanced-standing-form/wizard/FormWizardSections.vue'
import WizardPaginationActions from '@/components/current-student-advanced-standing-form/wizard/WizardPaginationActions.vue'
import WizardStepper from '@/components/current-student-advanced-standing-form/wizard/WizardStepper.vue'
import WorkExperienceModalHost from '@/components/current-student-advanced-standing-form/modals/work-experience/WorkExperienceModalHost.vue'
import {
  deleteSubmissionFile,
  editStudentSubmission,
  loadStudentForm,
  searchInstitutions,
  searchSubjects,
  StudentFormApiError,
  uploadSubmissionFile,
} from './composables/current-student-advanced-standing-form/useStudentFormApi'
import { usePublicFormWizard } from './composables/current-student-advanced-standing-form/usePublicFormWizard'
import { useModalShell } from './composables/current-student-advanced-standing-form/useModalShell'
import { useFineUploaderModalUploads } from './composables/current-student-advanced-standing-form/useFineUploaderModalUploads'
import { useCurrentStudentApplicationStore } from './stores/current-student-advanced-standing-form/applicationStore'
import { useCurrentStudentCourseSelectionStore } from './stores/current-student-advanced-standing-form/courseSelectionStore'
import {
  buildDeploymentUserState,
} from './models/current-student-advanced-standing-form/appSession'
import {
  buildDocumentationRows,
  hasMissingRequiredAttachments,
} from './models/current-student-advanced-standing-form/documentation'
import {
  courseSubjects,
  subjectDisplayCode,
} from './models/current-student-advanced-standing-form/display'
import {
  articulationConditions,
  articulationRows,
  courseRuleOutcomeRows,
  outcomePriorityClass,
  subjectPrecedentRows,
} from './models/current-student-advanced-standing-form/precedents'
import {
  canAddExternalSubject,
  canEditExternalCourse,
  canEditExternalSubject,
  canEditWorkExperience,
  emptyExternalSubjectsMessage,
  externalCourseSubjectRowspan,
  externalCourseRowClass,
  externalSubjectRowClass,
  isVerifiedStatus,
  workExperienceRowClass,
  workExperienceSubjectRowspan,
} from './models/current-student-advanced-standing-form/previousLearning'
import type {
  ExternalCourse,
  ExternalSubject,
  PublicFormApiResponse,
  SubjectRequest,
  WorkExperience,
} from './models/current-student-advanced-standing-form/api'

const missingWorkExperienceSubjectMessage = 'For each of your work experience items, you must add at least one subject to request credit for.'
const {
  wizardSteps,
  currentStepIndex,
  navigateToStep: navigateToWizardStep,
  navigateToPreviousStep,
  canNavigateToStep: canNavigateToWizardStep,
  stepClass: wizardStepClass,
  stepAriaDisabled: wizardStepAriaDisabled,
  stepAriaSelected: wizardStepAriaSelected,
} = usePublicFormWizard({
  onStepChanged: scrollToWizardTopAfterRender,
})
const applicationStore = useCurrentStudentApplicationStore()
const {
  secureData,
  courseLevelList,
  electiveList,
  externalCourses,
  previousLearningExternalCourses,
  externalCoursesWithSubjects,
  workExperiences,
  workExperienceMissingRequestedSubject,
  legacyUnspecifiedCreditDetected,
} = storeToRefs(applicationStore)
const {
  setSubmission,
} = applicationStore
const courseSelectionStore = useCurrentStudentCourseSelectionStore()
const {
  handbookUrl,
  showSpgTool,
} = storeToRefs(courseSelectionStore)
const documentationErrorMessage = ref('')
const applicationComplete = ref(false)

const {
  syncModalVisibility,
  modalIsActive,
  modalIsShown,
  installModalEventListeners,
  cleanupModalShell,
} = useModalShell({
  closeModal: closeModalById,
})

const {
  triggerUploadInput,
  closeFineUploaderAlert,
  showUploadLimitAlert,
  onUploadButtonKeydown,
  setUploadButtonHover,
  setUploadButtonFocus,
  revokeUploadThumbnailsFor,
  revokeAllUploadThumbnails,
  uploadThumbnailUrl,
  onUploadDragEnter,
  onUploadDragOver,
  onUploadDragLeave,
  onUploadDrop,
  onAttachmentSelected,
  cancelStagedUpload,
  uploadAttachment,
  retryUploadAttachment,
  deleteStagedUpload,
} = useFineUploaderModalUploads({
  uploadFile: uploadSubmissionFile,
  deleteFile: deleteSubmissionFile,
  uploadErrorMessage: getUploadErrorMessage,
  uploadDeleteErrorMessage: getUploadDeleteErrorMessage,
})

const modalUploadHelpers = {
  closeFineUploaderAlert,
  showUploadLimitAlert,
  onUploadDragEnter,
  onUploadDragOver,
  onUploadDragLeave,
  onUploadDrop,
  onAttachmentSelected,
  revokeUploadThumbnailsFor,
  uploadAttachment,
  retryUploadAttachment,
  deleteStagedUpload,
}

const externalCourseModalHostRef = ref<InstanceType<typeof ExternalCourseModalHost> | null>(null)
const externalSubjectModalHostRef = ref<InstanceType<typeof ExternalSubjectModalHost> | null>(null)
const workExperienceModalHostRef = ref<InstanceType<typeof WorkExperienceModalHost> | null>(null)
const subjectRequestModalHostRef = ref<InstanceType<typeof SubjectRequestModalHost> | null>(null)
const confirmDeleteModalHostRef = ref<InstanceType<typeof ConfirmDeleteModalHost> | null>(null)
const formWizardSectionsRef = ref<InstanceType<typeof FormWizardSections> | null>(null)

const documentationRows = computed(() => {
  return buildDocumentationRows(externalCourses.value, workExperiences.value, {
    editExternalCourse: openEditExternalCourseModal,
    editExternalSubject: openExternalSubjectModal,
    editWorkExperience: openEditWorkExperienceModal,
  })
})

const requiredAttachmentsMissing = computed(() => {
  return hasMissingRequiredAttachments(documentationRows.value)
})

onMounted(() => {
  installModalEventListeners()
  void pageInit()
})

onUnmounted(() => {
  cleanupModalShell()
  revokeAllUploadThumbnails()
})

function closeModalById(modalId: string): void {
  if (modalId === 'ExternalCourseModal') {
    externalCourseModalHostRef.value?.closeExternalCourseModal()
    return
  }

  if (modalId === 'ExternalSubjectModal') {
    externalSubjectModalHostRef.value?.closeExternalSubjectModal()
    return
  }

  if (modalId === 'WorkExperienceModal') {
    workExperienceModalHostRef.value?.closeWorkExperienceModal()
    return
  }

  if (modalId === 'SubjectSelectedConfirmModal') {
    subjectRequestModalHostRef.value?.closeSubjectSelectedConfirmModal()
    return
  }

  if (modalId === 'LtuSubjectModal') {
    subjectRequestModalHostRef.value?.closeLtuSubjectModal()
    return
  }

  if (modalId === 'ConfirmDeleteModal') {
    confirmDeleteModalHostRef.value?.cancelConfirmDelete()
  }
}

async function pageInit(): Promise<void> {
  formWizardSectionsRef.value?.startPageLoad()
  formWizardSectionsRef.value?.applyImpersonationQueryState(window.location.href)

  try {
    const result = await loadStudentForm()
    updateDeployModeUserName(result)
    formWizardSectionsRef.value?.applyMaintenanceState(result.data, result.user)
  } catch (error) {
    if (handleAuthSessionError(error, 'reload')) return

    formWizardSectionsRef.value?.showPageLoadError(getErrorMessage(error))
  }
}

async function goNext(event: Event): Promise<void> {
  event.preventDefault()
  if (applicationComplete.value) return

  if (currentStepIndex.value === 0) {
    const studentLogin = formWizardSectionsRef.value
    if (studentLogin == null || studentLogin.loginPendingOrHidden()) return

    if (studentLogin.isLoginValidated()) {
      navigateToWizardStep(1)
      return
    }

    await studentLogin.processLogin()
    return
  }

  if (currentStepIndex.value === 1) {
    await formWizardSectionsRef.value?.validateAndSaveCourseSelection()
    return
  }

  if (currentStepIndex.value === 2) {
    if (!canLeaveCurrentStep()) {
      return
    }

    navigateToWizardStep(3)
    return
  }

  if (currentStepIndex.value === 3) {
    navigateToWizardStep(4)
    return
  }

  if (currentStepIndex.value === 4) {
    if (subjectRequestModalHostRef.value?.shouldShowUnspecifiedCreditWarning(legacyUnspecifiedCreditDetected.value)) {
      subjectRequestModalHostRef.value.openSubjectSelectedConfirmModal()
      return
    }

    documentationErrorMessage.value = ''
    navigateToWizardStep(5)
    return
  }

  if (currentStepIndex.value === 5) {
    if (requiredAttachmentsMissing.value) {
      documentationErrorMessage.value = 'Please check all required documentation is attached'
      return
    }

    documentationErrorMessage.value = ''
    navigateToWizardStep(6)
  }
}

function goPrevious(event: Event): void {
  event.preventDefault()
  if (applicationComplete.value) return

  navigateToPreviousStep()
}

function goToStep(index: number, event: Event): void {
  event.preventDefault()
  if (canNavigateToWizardStep(index, applicationComplete.value)) {
    navigateToWizardStep(index)
  }
}

function canLeaveCurrentStep(): boolean {
  if (currentStepIndex.value === 2 && workExperienceMissingRequestedSubject.value) {
    window.alert(missingWorkExperienceSubjectMessage)
    return false
  }

  return true
}

function scrollToElementAfterRender(selector: string): void {
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView()
    })
  })
}

function scrollToWizardTopAfterRender(): void {
  scrollToElementAfterRender('#wizard')
}

function openSubjectRequestModal(externalSubject: ExternalSubject): void {
  subjectRequestModalHostRef.value?.openSubjectRequestModal(externalSubject)
}

function openCourseSubjectRequestModal(externalCourse: ExternalCourse): void {
  subjectRequestModalHostRef.value?.openCourseSubjectRequestModal(externalCourse)
}

function openWorkExpSubjectRequestModal(workExperience: WorkExperience): void {
  subjectRequestModalHostRef.value?.openWorkExpSubjectRequestModal(workExperience)
}

function confirmExternalSubjectRequestRemoval(subject: ExternalSubject, request: SubjectRequest): void {
  subjectRequestModalHostRef.value?.confirmExternalSubjectRequestRemoval(subject, request)
}

function confirmExternalCourseRequestRemoval(course: ExternalCourse, request: SubjectRequest): void {
  subjectRequestModalHostRef.value?.confirmExternalCourseRequestRemoval(course, request)
}

function confirmWorkExperienceSubjectRequestRemoval(workExperience: WorkExperience, request: SubjectRequest): void {
  subjectRequestModalHostRef.value?.confirmWorkExperienceSubjectRequestRemoval(workExperience, request)
}

function openConfirmDelete(message: string, action: () => Promise<void>): void {
  confirmDeleteModalHostRef.value?.openConfirmDelete(message, action)
}

function onContinuePastUnspecifiedWarning(): void {
  documentationErrorMessage.value = ''
  navigateToWizardStep(5)
}

function onApplicationCompleteChanged(complete: boolean): void {
  applicationComplete.value = complete
}

function updateDeployModeUserName(response: PublicFormApiResponse<unknown>): void {
  const deploymentUserState = buildDeploymentUserState(response, {
    deploymentMode: window.localStorage.getItem('defaultDeployMode'),
    userName: window.localStorage.getItem('currentAsdUserName'),
    permissions: window.localStorage.getItem('currentAsdUserPerms'),
  })

  if (deploymentUserState.defaultDeployModeStorageValue !== undefined) {
    window.localStorage.setItem('defaultDeployMode', deploymentUserState.defaultDeployModeStorageValue)
  }

  document.body.setAttribute('data-deploymode', deploymentUserState.deploymentMode)

  if (deploymentUserState.userNameStorageValue === null) {
    window.localStorage.removeItem('currentAsdUserName')
  } else if (deploymentUserState.userNameStorageValue !== undefined) {
    window.localStorage.setItem('currentAsdUserName', deploymentUserState.userNameStorageValue)
  }

  if (deploymentUserState.permissionsStorageValue === null) {
    window.localStorage.removeItem('currentAsdUserPerms')
  } else if (deploymentUserState.permissionsStorageValue !== undefined) {
    window.localStorage.setItem('currentAsdUserPerms', deploymentUserState.permissionsStorageValue)
  }

  if (deploymentUserState.userAuthenticated) {
    document.body.classList.add('userAuthenticated')
  }

  if (deploymentUserState.permissions) {
    document.body.setAttribute('data-perms', deploymentUserState.permissions)
  }
}

function handleAuthSessionError(error: unknown, behavior: 'redirect' | 'reload'): boolean {
  if (!(error instanceof StudentFormApiError) || error.status !== 401) return false

  if (behavior === 'redirect') {
    redirectToAuthentication()
    return true
  }

  window.location.reload()
  return true
}

function redirectToAuthentication(): void {
  const redirectLink = document.createElement('a')
  redirectLink.href = `../api/system/authenticate?return=${encodeURIComponent(window.location.href)}`
  window.location.replace(redirectLink.href)
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function getUploadErrorMessage(error: unknown): string {
  if (error instanceof StudentFormApiError && error.status === 401) return 'Upload failed'

  return getErrorMessage(error)
}

function getUploadDeleteErrorMessage(error: unknown): string {
  if (error instanceof StudentFormApiError && error.status === 401) return 'Delete failed'

  return getErrorMessage(error)
}

function openAddExternalCourseModal(): void {
  externalCourseModalHostRef.value?.openAddExternalCourseModal()
}

function openEditExternalCourseModal(course: ExternalCourse): void {
  externalCourseModalHostRef.value?.openEditExternalCourseModal(course)
}

function openAddExternalSubjectModal(course: ExternalCourse): void {
  externalSubjectModalHostRef.value?.openAddExternalSubjectModal(course)
}

function openEditExternalSubjectModal(subject: ExternalSubject, course: ExternalCourse): void {
  externalSubjectModalHostRef.value?.openEditExternalSubjectModal(subject, course)
}

function openExternalSubjectModal(subject: ExternalSubject): void {
  externalSubjectModalHostRef.value?.openExternalSubjectModal(subject)
}

function openAddWorkExperienceModal(): void {
  workExperienceModalHostRef.value?.openAddWorkExperienceModal()
}

function openEditWorkExperienceModal(workExperience: WorkExperience): void {
  workExperienceModalHostRef.value?.openEditWorkExperienceModal(workExperience)
}

</script>

<template>
  <div class="content">
    <ExternalCourseModalHost
      ref="externalCourseModalHostRef"
      :course-level-list="courseLevelList"
      :secure-data="secureData"
      :modal-is-shown="modalIsShown"
      :modal-is-active="modalIsActive"
      :sync-modal-visibility="syncModalVisibility"
      :search-institutions="searchInstitutions"
      :edit-student-submission="editStudentSubmission"
      :set-submission="setSubmission"
      :handle-auth-session-error="handleAuthSessionError"
      :get-error-message="getErrorMessage"
      :open-confirm-delete="openConfirmDelete"
      :upload-helpers="modalUploadHelpers"
      :trigger-upload-input="triggerUploadInput"
      :upload-button-keydown="onUploadButtonKeydown"
      :set-upload-button-hover="setUploadButtonHover"
      :set-upload-button-focus="setUploadButtonFocus"
      :cancel-staged-upload="cancelStagedUpload"
      :upload-thumbnail-url="uploadThumbnailUrl"
    />

    <ExternalSubjectModalHost
      ref="externalSubjectModalHostRef"
      :secure-data="secureData"
      :modal-is-shown="modalIsShown"
      :modal-is-active="modalIsActive"
      :sync-modal-visibility="syncModalVisibility"
      :edit-student-submission="editStudentSubmission"
      :set-submission="setSubmission"
      :handle-auth-session-error="handleAuthSessionError"
      :get-error-message="getErrorMessage"
      :open-confirm-delete="openConfirmDelete"
      :upload-helpers="modalUploadHelpers"
      :trigger-upload-input="triggerUploadInput"
      :upload-button-keydown="onUploadButtonKeydown"
      :set-upload-button-hover="setUploadButtonHover"
      :set-upload-button-focus="setUploadButtonFocus"
      :cancel-staged-upload="cancelStagedUpload"
      :upload-thumbnail-url="uploadThumbnailUrl"
    />

    <SubjectRequestModalHost
      ref="subjectRequestModalHostRef"
      :external-courses="externalCourses"
      :elective-list="electiveList"
      :secure-data="secureData"
      :modal-is-shown="modalIsShown"
      :modal-is-active="modalIsActive"
      :sync-modal-visibility="syncModalVisibility"
      :search-subjects="searchSubjects"
      :edit-student-submission="editStudentSubmission"
      :set-submission="setSubmission"
      :handle-auth-session-error="handleAuthSessionError"
      :get-error-message="getErrorMessage"
      :open-confirm-delete="openConfirmDelete"
      @continue-past-unspecified-warning="onContinuePastUnspecifiedWarning"
    />

    <WorkExperienceModalHost
      ref="workExperienceModalHostRef"
      :secure-data="secureData"
      :modal-is-shown="modalIsShown"
      :modal-is-active="modalIsActive"
      :sync-modal-visibility="syncModalVisibility"
      :edit-student-submission="editStudentSubmission"
      :set-submission="setSubmission"
      :handle-auth-session-error="handleAuthSessionError"
      :get-error-message="getErrorMessage"
      :open-confirm-delete="openConfirmDelete"
      :upload-helpers="modalUploadHelpers"
      :trigger-upload-input="triggerUploadInput"
      :upload-button-keydown="onUploadButtonKeydown"
      :set-upload-button-hover="setUploadButtonHover"
      :set-upload-button-focus="setUploadButtonFocus"
      :cancel-staged-upload="cancelStagedUpload"
      :upload-thumbnail-url="uploadThumbnailUrl"
    />

    <ConfirmDeleteModalHost
      ref="confirmDeleteModalHostRef"
      :modal-is-shown="modalIsShown"
      :modal-is-active="modalIsActive"
      :sync-modal-visibility="syncModalVisibility"
      :handle-auth-session-error="handleAuthSessionError"
      :get-error-message="getErrorMessage"
    />

    <div class="container">
      <div class="headerbox">
        <img class="logo" alt="La Trobe Logo" src="/img/La_Trobe_University_logo.svg">
      </div>

      <div id="wizard" class="wizard form-vue-shell">
        <WizardStepper
          :steps="wizardSteps"
          :application-complete="applicationComplete"
          :step-class="wizardStepClass"
          :step-aria-disabled="wizardStepAriaDisabled"
          :step-aria-selected="wizardStepAriaSelected"
          :on-step-click="goToStep"
        />

        <FormWizardSections
          ref="formWizardSectionsRef"
          :current-step-index="currentStepIndex"
          :secure-data="secureData"
          :previous-learning-external-courses="previousLearningExternalCourses"
          :work-experiences="workExperiences"
          :external-courses="externalCourses"
          :external-courses-with-subjects="externalCoursesWithSubjects"
          :work-experience-missing-requested-subject="workExperienceMissingRequestedSubject"
          :missing-work-experience-subject-message="missingWorkExperienceSubjectMessage"
          :handbook-url="handbookUrl"
          :show-spg-tool="showSpgTool"
          :documentation-rows="documentationRows"
          :documentation-error-message="documentationErrorMessage"
          :handle-auth-session-error="handleAuthSessionError"
          :get-error-message="getErrorMessage"
          :external-course-row-class="externalCourseRowClass"
          :external-course-subject-rowspan="externalCourseSubjectRowspan"
          :work-experience-row-class="workExperienceRowClass"
          :work-experience-subject-rowspan="workExperienceSubjectRowspan"
          :is-verified-status="isVerifiedStatus"
          :can-edit-external-course="canEditExternalCourse"
          :can-edit-work-experience="canEditWorkExperience"
          :empty-external-subjects-message="emptyExternalSubjectsMessage"
          :external-subject-row-class="externalSubjectRowClass"
          :can-edit-external-subject="canEditExternalSubject"
          :can-add-external-subject="canAddExternalSubject"
          :course-subjects="courseSubjects"
          :subject-precedent-rows="subjectPrecedentRows"
          :subject-display-code="subjectDisplayCode"
          :outcome-priority-class="outcomePriorityClass"
          :articulation-rows="articulationRows"
          :articulation-conditions="articulationConditions"
          :course-rule-outcome-rows="courseRuleOutcomeRows"
          @update-deploy-user="updateDeployModeUserName"
          @login-success="navigateToWizardStep(1)"
          @course-saved="navigateToWizardStep(2)"
          @add-external-course="openAddExternalCourseModal"
          @edit-external-course="openEditExternalCourseModal"
          @add-external-course-subject-request="openCourseSubjectRequestModal"
          @delete-external-course-subject-request="confirmExternalCourseRequestRemoval"
          @add-work-experience="openAddWorkExperienceModal"
          @edit-work-experience="openEditWorkExperienceModal"
          @add-work-experience-subject-request="openWorkExpSubjectRequestModal"
          @delete-work-experience-subject-request="confirmWorkExperienceSubjectRequestRemoval"
          @edit-subject="openEditExternalSubjectModal"
          @add-subject="openAddExternalSubjectModal"
          @open-subject-request="openSubjectRequestModal"
          @remove-subject-request="confirmExternalSubjectRequestRemoval"
          @application-complete-changed="onApplicationCompleteChanged"
        />

        <WizardPaginationActions
          :visible="!applicationComplete"
          :previous-disabled="currentStepIndex === 0"
          :next-visible="currentStepIndex !== 6"
          @previous="goPrevious"
          @next="goNext"
        />
      </div>
    </div>
  </div>
</template>
